<script setup>
  import abilities from "@/composables/abilities";
  import {computed, nextTick, onMounted, onUpdated, ref, watch, defineProps, shallowRef} from "vue";
  import SubLayout from "@/views/Layouts/partials/SubLayout.vue";
  import Card from "@/Modules/Financial/components/Card";
  import Button from "@/components/Button.vue";
  import {useStore} from "vuex";
  import AccountModal from "@/Modules/Financial/components/Modals/Account/AccountModal"
  import DepositModal from "@/Modules/Financial/components/Modals/Account/DepositModal"
  import WithdrawalModal from "@/Modules/Financial/components/Modals/Account/WithdrawalModal"
  import TransferModal from "@/Modules/Financial/components/Modals/Account/TransferModal"
  import TransactionAccountModal from "@/Modules/Financial/components/Modals/TransactionModal"
  import { useRoute } from 'vue-router';
  import Select2 from "@/components/Select2.vue";
  import {formattedLocalAmount} from "@/mixins/Helpers";
  import {useFormatAmount} from "@/composables/formatAmount";
  import {useFormatDate} from "@/composables/formatDate";
import Img from "@/components/Img.vue";
  import i18n from "@/plugins/i18n";

  const props = defineProps({
    office: {
      type: String,
      required: false
    },
    society: {
      type: String,
      required: false
    },
    filterStatAccount: {
      type: Object,
      required: true
    },
  })

  const store = useStore()
  const route = useRoute()

  const monthsName = ref(['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'])
  const monthNumbers = ref([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12])

  const currentMonth = ref(null)
  const transactionAccount = ref(null)
  const currentAccount = ref(null)
  const range = ref(null)
  const histories = ref([])
  const count = ref(0)
  const accountsLoaded = ref(false)
  const currencyIso = ref('')
  const monthStatistics = ref({
    currency_iso: null,
    statistics: {}
  })
  const formAccount = ref({
    name: '',
    bank: '',
    account_number: '',
    initial_amount: 0,
    currency_id: '',
    society_id: props.society
  })

  const formEditAccount = ref({
    id: '',
    name: '',
    bank: '',
    initial_amount: 0,
    account_number: '',
    society_id: props.society
  })

  const formDeposit = ref({
    account_id: '',
    amount: 0,
    reason: "",
  })

  const formWithdrawal = ref({
    account_id: '',
    amount: 0,
    reason: "",
    expense_created: false,
    expense_reason: '',
    invoices: [],
    expense_amount: 0,
    expense_date: '',
    office_id: props.office
  })

  const formTransfer = ref({
    account_id: '',
    type: 'account',
    credit_account_id: '',
    amount: 0,
    rate: 1,
    reason: '',
    debit_account_id: ''
  })

  const accountData = ref({})

  const xAxisRoot = ref(null);
  const mainSeries = ref([]);
  const accountChart = shallowRef()

  const amountFormatter = useFormatAmount()
  const dateFormatter = useFormatDate()

  const accounts = computed(() => store.getters['accounts/getAccounts'])
  const boxes = computed(() => store.getters['accounts/getBoxes'])
  const getAccountsToCredit = computed(() => Object.values(accounts.value || {}).filter(a => a.id != currentAccount.value))

  watch(() => props.office, async(newValue, oldValue) => {
    await loadAccounts()

    let firstAccount = Object.values(accounts.value)[0];
    if (firstAccount) {
      currentAccount.value = firstAccount.id;
    }else{
      currentAccount.value = null
      histories.value = []

      formEditAccount.value = {
        id: '',
        name: '',
        bank: '',
        initial_amount: 0,
        account_number: '',
        society_id: props.society
      }

      monthStatistics.value = {
        currency_iso: null,
        statistics: {}
      }

      initChart([], '')
    }
  })

  watch(() => accounts.value, async(newValue, oldValue) => {
    if(newValue){
      updateCurrentAccount(currentAccount.value)
      if (props.filterStatAccount.start_date && props.filterStatAccount.end_date) {
        await LoadTransactionsData();
      }
      if (currentMonth.value) {
        initialiseMonthData(currentMonth.value);
      }
      await initDateRangePicker()
    }
  })

  watch(() => currentAccount.value, async (newValue, oldValue) => {
    if (newValue && newValue !== oldValue) {
      updateCurrentAccount(newValue);
      if (props.filterStatAccount.start_date && props.filterStatAccount.end_date) {
        await LoadTransactionsData();
      }
      if (currentMonth.value) {
        initialiseMonthData(currentMonth.value);
      }
      initialiseData();
      loadAccount()
      await initDateRangePicker()
    }
  })

  watch(() => currentMonth.value, () => {
    if(currentMonth.value) {
      initialiseMonthData(currentMonth.value)
    }
  })

  watch(() => props.filterStatAccount.start_date, async (newValue, oldValue) => {
    if (newValue) {
      range.value = moment(newValue).format('YYYY')
      LoadTransactionsData()
      initialiseMonthData()
      initialiseData()
    }
  });

  watch(() => props.filterStatAccount.end_date, async (newValue, oldValue) => {
    if (newValue) {
      initialiseData();
      LoadTransactionsData()
    }
  });

  const getCurrentAccount = computed(() => store.getters['accounts/getAccount'](currentAccount.value) || {})

  const reversedHistories = computed(() => [...histories.value].reverse());

  const currency = computed(() => {
    return Object.values(getCurrentAccount.value).length ? {
      iso: getCurrentAccount.value.currency_iso,
      symbol: getCurrentAccount.value.currency_symbol,
    }: {
      iso: "",
      symbol: "",
    }
  })

  function loadAccount(){
    loadAccounts()
    updateCurrentAccount(formEditAccount.value.id)
  }

  function updateCurrentAccount(id) {
    let account = Object.values(accounts.value || {}).find(a => a.id == id)
    if(account){
      formEditAccount.value.id = account.id
      formEditAccount.value.name = account.name
      formEditAccount.value.bank = account.bank
      formEditAccount.value.initial_amount = account.amount
      formEditAccount.value.account_number = account.account_number
      currencyIso.value = account.currency_iso
      formDeposit.value.account_id = account.id;
      formWithdrawal.value.account_id = account.id;
      formTransfer.value.account_id = account.id;
      formTransfer.value.debit_account_id = account.id;
      accountData.value = account
    }
  }

  function openAccountModal(name) {
    $('#account_modal_'+name).modal('toggle')
  }

  function openDepositModal() {
    $('#deposit_modal').modal('toggle')
  }

  function openWithdrawalModal() {
    $('#withdrawal_modal').modal('toggle')
  }

  function openTransferModal() {
    $('#transfer_modal').modal('toggle')
  }

  async function loadAccounts() {
    if(props.society){
      await store.dispatch('accounts/loadAccountsForCountry', props.society)
    }
  }

  function setCurrentAccount(account){
    currentAccount.value = account
  }

  onMounted(async () => {
    await loadAccounts()
    accountsLoaded.value = true
    if(props.office) {
      await store.dispatch('accounts/loadBoxesForOffice', props.office)
    }
    store.dispatch('currencies/loadCurrencies')

    if(accounts.value){
      let firstAccount = Object.values(accounts.value)[0];
      if (firstAccount) {
        currentAccount.value = firstAccount.id;
      }
    }

    if(props.filterStatAccount.start_date && props.filterStatAccount.end_date){
      await LoadTransactionsData();
    }

    await initDateRangePicker()
  })

  async function reloadData() {
    LoadTransactionsData()
    initialiseMonthData()
    loadAccount()
    await initDateRangePicker()
    if(props.office) {
      await store.dispatch('accounts/loadBoxesForOffice', props.office)
    }
  }

  async function LoadTransactionsData() {
    if(currentAccount.value){
      await axios.get(`/get/account/${currentAccount.value}/histories?startDate=${props.filterStatAccount.start_date}&endDate=${props.filterStatAccount.end_date}`)
          .then((response) => {
            histories.value = response.data.histories
            count.value = response.data.count
          })
          .catch((error) => {
          })
    }
  }

  function initChart(dataSeries) {
    if(!xAxisRoot.value && !mainSeries.value.length) {
      am5.ready(() => {

        var root = am5.Root.new("kt_charts_widget_1_chart", {
          panX: "none",
          panY: "none",
          maxZoomLevel: 1
        });

        root.setThemes([
          am5themes_Animated.new(root)
        ]);

        accountChart.value = root.container.children.push(am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          paddingLeft: 0,
          wheelX: "none",
          wheelY: "none",
          layout: root.verticalLayout
        }));

        var legend = accountChart.value.children.push(
            am5.Legend.new(root, {
              centerX: am5.p50,
              x: am5.p50
            })
        );

        var data = dataSeries


        var xRenderer = am5xy.AxisRendererX.new(root, {
          cellStartLocation: 0.1,
          cellEndLocation: 0.9,
          minorGridEnabled: true,
          minGridDistance: 30
        })

        var xAxis = accountChart.value.xAxes.push(am5xy.CategoryAxis.new(root, {
          categoryField: "month",
          renderer: xRenderer,
          tooltip: am5.Tooltip.new(root, {})
        }));

        xAxisRoot.value = xAxis

        xRenderer.grid.template.setAll({
          location: 1
        })

        xAxis.data.setAll(data);

        var yAxis = accountChart.value.yAxes.push(am5xy.ValueAxis.new(root, {
          renderer: am5xy.AxisRendererY.new(root, {
            strokeOpacity: 0.1
          })
        }));

        const makeSeries = (name, fieldName, color) => {
          var series = accountChart.value.series.push(am5xy.ColumnSeries.new(root, {
            name: name,
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: fieldName,
            categoryXField: "month",
          }));

          series.columns.template.setAll({
            tooltipText: "{name}, {categoryX}:{valueY}",
            width: am5.percent(90),
            tooltipY: 0,
            strokeOpacity: 0,
          });

          series.data.setAll(data);

          series.appear();

          series.bullets.push(function () {
            return am5.Bullet.new(root, {
              locationY: 0,
              sprite: am5.Label.new(root, {
                text: "{valueY}",
                fill: root.interfaceColors.get("alternativeText"),
                centerY: 0,
                centerX: am5.p50,
                populateText: true
              })
            });
          });

          legend.data.push(series);

          series.columns.template.setAll({
            fill: am5.color(color), stroke: am5.color(color),
            maxWidth: 10
          });

          mainSeries.value[fieldName] = series
        }

        makeSeries("Crédit", "credit", 0x2DA333);
        makeSeries("Débit", "debit", 0xF64E60);
      });
    }else {
      xAxisRoot.value.data.setAll(dataSeries);
      mainSeries.value['credit'].data.setAll(dataSeries);
      mainSeries.value['debit'].data.setAll(dataSeries);
    }
  }

  async function initialiseMonthData(current) {
    if (current === undefined){
      current = parseInt(moment().format('M'))
    }

    let date = range.value || (props.filterStatAccount.start_date ? moment(props.filterStatAccount.start_date).format('YYYY') : null);

    if(date && currentAccount.value) {
      let response = await store.dispatch('accounts/accountMonthStatistics', {
        account_id: currentAccount.value,
        year: date,
        month: current
      })

      if('currency_iso' in response.data){
        monthStatistics.value = response.data
      }
    }
  }

  async function initDateRangePicker(){
    var start = moment().subtract(29, "days");
    var end = moment();

    $("#kt_dashboard_daterangepicker").datepicker({
      format: "yyyy",
      startView: "years",
      minViewMode: "years",
      autoclose: true,
      maxDate: moment(new Date())
    }, cb);

    currentMonth.value = parseInt(moment().format('M'))

    await cb(start, end, '');
  }

  async function cb(start, end, label) {
    let title = '';

    let year = end.format('Y');

    range.value = year

    $('#kt_dashboard_daterangepicker_date').html(year);
    $('#kt_dashboard_daterangepicker_title').html(title);

    if(currentMonth.value){
      initialiseMonthData(currentMonth.value)
    }else {
      initialiseMonthData()
    }

    initialiseData(year)
  }

  async function initialiseData(range){
    let date = range || (props.filterStatAccount.start_date ? moment(props.filterStatAccount.start_date).format('YYYY') : null);

    if (moment(date, 'YYYY', true).isValid()){
      let response = await store.dispatch('accounts/accountStatistics', {
        account_id: currentAccount.value,
        year: date
      })

      let data = [];

      monthNumbers.value.forEach(number => {
        let information = response?.data?.statistics[number]
        if(information){
          data.push({
            "month": information.month,
            "credit": information.deposits_value,
            "debit": information.debits_value,
          })
        }
      })

      initChart(data, response?.data?.currency_iso)
    }
  }

  async function viewTransactionDetails(id){
    if(id){
      let response = await store.dispatch('accounts/loadDetailTransaction', id)
      transactionAccount.value = response.data.transaction
      if(transactionAccount.value) {
        $('#detailsTransactionAccount').modal('toggle')
      }
    }
  }

  function downloadAccountHistoric() {
    const data = {
      id: currentAccount.value,
      startDate: props.filterStatAccount.start_date,
      endDate: props.filterStatAccount.end_date
    }
    store.dispatch('accounts/getAccountHistoricFile', data)
        .then(res => {
          const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] }));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', accountData.value.name+'_transactions_'+data.startDate+ '_'+ data.endDate +'.xls');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.toastr['success'](i18n.global.t('notification.download_transactions'))
        })
        .catch((error) => {

        }).finally(() => {})
  }
  
</script>

<template>
  <div class="row">
    <div class="col-md-3">
      <Card title="financial.accounts.info_account" containerClass="mb-5 shadow-sm" titleContainerClass="d-flex justify-content-center">
          <template #cardBody>
            <div class="form-group row">
              <div class="col-lg-12 col-md-12 col-sm-12">
                <div class="input-group mb-5">
                  <div class="input-group-prepend">
                    <button v-if="$can(abilities.UPDATE_ACCOUNT)" @click="openAccountModal('edit')" class="btn btn-icon btn-primary" type="button">
                      <i class="fa fa-edit pr-0"></i>
                    </button>
                  </div>
                  <select id="accountSelect" v-model="currentAccount" class="form-control selectpicker" data-size="5" data-live-search="true">
                    <option :value="account.id" v-for="account in accounts" :key="account.id">{{ account.name }}</option>
                  </select>
                  <div class="input-group-prepend">
                    <button @click="openAccountModal('create')" v-if="$can(abilities.CREATE_ACCOUNT)" class="btn btn-icon btn-primary" type="button">
                      <i class="fa fa-plus pr-0"></i>
                    </button>
                  </div>
                </div>

                <div class="alert alert-warning my-4 text-center" role="alert" v-if="accounts.length == 0">
                  {{ $t('financial.accounts.account_empty') }}
                </div>

                <div class="d-flex align-items-center justify-content-center mb-5" v-if="accountData.amount">
                  <div class="text-center">
                    <span class="font-weight-bold font-size-h6 text-dark-75 text-hover-primary">{{accountData.name}}</span>
                    <div class="text-muted">{{formattedLocalAmount(accountData.amount)}} {{ currencyIso }}</div>
                  </div>
                </div>

                <div class="pt-8 pb-6">
                  <div class="d-flex align-items-center justify-content-between mb-2" v-if="accountData.bank">
                    <span class="font-weight-bold mr-2">{{ $t('financial.accounts.modal_account.bank') }}:</span>
                    <a href="#" class="text-muted text-hover-primary">{{accountData.bank}}</a>
                  </div>
                  <div class="d-flex align-items-center justify-content-between mb-2" v-if="accountData.account_number">
                    <span class="font-weight-bold mr-2">{{ $t('financial.accounts.modal_account.number') }}:</span>
                    <a href="#" class="text-muted text-hover-primary">{{accountData.account_number}}</a>
                  </div>
                </div>
              </div>
            </div>
          </template>
      </Card>
      <Card v-if="currentAccount && $can([abilities.DEPOSIT_ACCOUNT, abilities.WITHDRAWAL_ACCOUNT, abilities.TRANSFER_ACCOUNT])" title="financial.accounts.action" containerClass="mb-5 shadow-sm" titleContainerClass="d-flex justify-content-center">
          <template #cardBody>
              <div class="row text-center">
              <a v-if="$can(abilities.DEPOSIT_ACCOUNT)" class="btn btn-light-success font-weight-bold py-4 px-6 mb-4 text-center btn-block" @click="openDepositModal">{{ $t('financial.accounts.accounting_deposit') }}</a>
              <a v-if="$can(abilities.WITHDRAWAL_ACCOUNT)" class="btn btn-light-danger font-weight-bold py-4 px-6 mb-4 text-center btn-block" @click="openWithdrawalModal">{{ $t('financial.accounts.accounting_withdrawal') }}</a>
              <a v-if="$can(abilities.TRANSFER_ACCOUNT)" class="btn btn-light-warning font-weight-bold py-4 px-6 mb-4 text-center btn-block" @click="openTransferModal">{{ $t('financial.accounts.accounting_transfer') }}</a>
            </div>
          </template>
      </Card>
    </div>
    <div class="col-md-9">
      <Card containerClass="card shadow-sm mb-5">
        <div class="card-header card-header-tabs-line card-header-stretch">
          <div class="card-toolbar">
            <ul class="nav nav-tabs nav-line-tabs nav-stretch fs-6 border-0">
              <li v-if="$can(abilities.VIEW_STAT_ACCOUNT)" class="nav-item">
                <a class="nav-link active btn-active-light" data-bs-toggle="tab" href="#kt_tab_statistics1">
                  <span class="nav-icon">
                    <i class="fa fa-chart-bar fa-2x me-3"></i>
                  </span>
                  <span class="nav-text fw-bold fs-3">{{$t('financial.accounts.stats')}}</span>
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" data-bs-toggle="tab" href="#kt_tab_historic1">
                  <span class="nav-icon">
                    <i class="fa fa-history fa-2x me-3"></i>
                  </span>
                  <span class="nav-text fw-bold fs-3">{{$t('financial.accounts.historics')}}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
        <template #cardBody>
          <div class="tab-content" id="myTabContent">
            <div class="tab-pane fade show active" id="kt_tab_statistics1" role="tabpanel">
              <div class="row">
                <div class="col-md-12">
                  <div id="kt_charts_widget_1_chart"></div>
                  <div class="row mt-4 border-bottom pb-10">
                    <div class="col-md-12 text-center">
                      <button v-for="(monthName, key) in monthsName" @click="currentMonth = key + 1" type="button" :key="key" :class="[currentMonth - 1 === key ? 'btn-primary': 'btn-light']" class="btn btn-shadow font-weight-bold me-2 mb-2">{{ monthName }}</button>
                    </div>
                  </div>
                  <div v-if="Object.values(monthStatistics.statistics).length">
                    <div class="col-md-12 mt-10 border-bottom pb-10">
                      <h1>{{$t('financial.accounts.credit_amount')}}</h1>
                      <div class="row mt-5">
                        <div class="col-12 col-md-4 col-lg-4 mb-4">
                          <div class="card p-3 h-100" style="background-color: #0072CE0D;">
                            <div class="d-flex flex-stack">
                              <div class="symbol symbol-40px me-4">
                                <div class="symbol-label fs-2 py-10 px-8 bg-white">
                                  <span class="text-success">{{ monthStatistics.statistics.deposits?.deposits_number }}</span>
                                </div>
                              </div>
                              <div class="d-flex align-items-center flex-row-fluid flex-wrap">
                                <div class="flex-grow-1 me-2">
                                  <a class="text-black fs-6 fw-bolder">
                                    {{$t('financial.accounts.deposits')}}
                                  </a>
                                  <div class="text-success fs-3">{{ amountFormatter.formatNumber(monthStatistics.statistics.deposits?.deposits_value) }} <span class="text-dark-50 font-weight-bold">{{ monthStatistics.currency_iso }}</span></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="col-12 col-md-4 col-lg-4 mb-4">
                          <div class="card p-3 h-100" style="background-color: #0072CE0D;">
                            <div class="d-flex flex-stack">
                              <div class="symbol symbol-40px me-4">
                                <div class="symbol-label fs-2 py-10 px-8 bg-white">
                                  <span class="text-success">{{ monthStatistics.statistics.deposits?.transfers_addition_number }}</span>
                                </div>
                              </div>
                              <div class="d-flex align-items-center flex-row-fluid flex-wrap">
                                <div class="flex-grow-1 me-2">
                                  <a class="text-black fs-6 fw-bolder">
                                    {{$t('financial.accounts.deposits_transfer')}}
                                  </a>
                                  <div class="text-success fs-3">{{ amountFormatter.formatNumber(monthStatistics.statistics.deposits?.transfers_addition_value) }} <span class="text-dark-50 font-weight-bold">{{ monthStatistics.currency_iso }}</span></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-12 mt-10">
                      <h1>{{$t('financial.accounts.debit_amount')}}</h1>
                      <div class="row mt-5">
                        <div class="col-12 col-md-4 col-lg-4 mb-4">
                          <div class="card p-3 h-100" style="background-color: #0072CE0D;">
                            <div class="d-flex flex-stack">
                              <div class="symbol symbol-40px me-4">
                                <div class="symbol-label fs-2 py-10 px-8 bg-white">
                                  <span class="text-danger">{{ monthStatistics.statistics.debits?.debits_number }}</span>
                                </div>
                              </div>
                              <div class="d-flex align-items-center flex-row-fluid flex-wrap">
                                <div class="flex-grow-1 me-2">
                                  <a class="text-black fs-6 fw-bolder">
                                    {{$t('financial.accounts.withdrawals')}}
                                  </a>
                                  <div class="text-danger fs-3">{{ amountFormatter.formatNumber(monthStatistics.statistics.debits?.debits_value) }} <span class="text-dark-50 font-weight-bold">{{ monthStatistics.currency_iso }}</span></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="col-12 col-md-4 col-lg-4 mb-4">
                          <div class="card p-3 h-100" style="background-color: #0072CE0D;">
                            <div class="d-flex flex-stack">
                              <div class="symbol symbol-40px me-4">
                                <div class="symbol-label fs-2 py-10 px-8 bg-white">
                                  <span class="text-danger">{{ monthStatistics.statistics.debits?.debits_expense_number }}</span>
                                </div>
                              </div>
                              <div class="d-flex align-items-center flex-row-fluid flex-wrap">
                                <div class="flex-grow-1 me-2">
                                  <a class="text-black fs-6 fw-bolder">
                                    {{$t('financial.accounts.withdrawals_expenses')}}
                                  </a>
                                  <div class="text-danger fs-3">{{ amountFormatter.formatNumber(monthStatistics.statistics.debits?.debits_expense_value) }} <span class="text-dark-50 font-weight-bold">{{ monthStatistics.currency_iso }}</span></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="col-12 col-md-4 col-lg-4 mb-4">
                          <div class="card p-3 h-100" style="background-color: #0072CE0D;">
                            <div class="d-flex flex-stack">
                              <div class="symbol symbol-40px me-4">
                                <div class="symbol-label fs-2 py-10 px-8 bg-white">
                                  <span class="text-danger">{{ monthStatistics.statistics.debits?.transfers_subtract_number }}</span>
                                </div>
                              </div>
                              <div class="d-flex align-items-center flex-row-fluid flex-wrap">
                                <div class="flex-grow-1 me-2">
                                  <a class="text-black fs-6 fw-bolder">
                                    {{$t('financial.accounts.transfers')}}
                                  </a>
                                  <div class="text-danger fs-3">{{ amountFormatter.formatNumber(monthStatistics.statistics.debits?.transfers_subtract_value) }} <span class="text-dark-50 font-weight-bold">{{ monthStatistics.currency_iso }}</span></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="tab-pane fade" id="kt_tab_historic1" role="tabpanel">
              <div class="row">
                <div class="form-group col-7">
                  <Button type="new" icon-class="fa fa-download" btn-class="btn btn-sm btn-icon me-2" @click.prevent="downloadAccountHistoric" />
                </div>
              </div>
              <div class="table-responsive tableFixHead">
                <table class="table align-middle table-row-bordered fs-6 gy-3 gx-4" style="margin-top: 13px !important; cursor:pointer">
                  <thead>
                    <tr class="fw-bold fs-6" style="background-color: #F2F3F4;">
                      <th width="30%" class="text-center d-none d-md-table-cell" scope="col">{{$t('financial.accounts.date')}}</th>
                      <th width="30%" class="text-center d-none d-md-table-cell" scope="col">{{$t('financial.accounts.description')}}</th>
                      <th width="30%" class="text-center" scope="col">{{$t('financial.accounts.debit')}}</th>
                      <th width="30%" class="text-center" scope="col">{{$t('financial.accounts.credit')}}</th>
                      <th class="text-center" scope="col">{{$t('financial.accounts.sold')}}</th>
                    </tr>
                  </thead>
                  <tbody id="tbodyId" v-if="histories.length">
                    <tr @click.prevent="viewTransactionDetails(history.id)" v-for="history in reversedHistories" style="cursor: pointer">
                      <td class="text-center d-none d-md-table-cell">{{ dateFormatter.formatSampleMyDateWithoutHour(history.created_at) }}</td>
                      <td class="text-center d-none d-md-table-cell">{{ history.description }}</td>
                      <td class="text-center">
                        <span class="text-danger font-weight-boldest">{{ history.type === 'DEBIT' ? amountFormatter.formatNumber(history.amountTotal) + ' ' + currency.iso : '' }}</span>
                      </td>
                      <td class="text-center">
                        <span class="text-success font-weight-boldest">{{ history.type === 'CREDIT' ? amountFormatter.formatNumber(history.amountTotal) + ' ' + currency.iso : '' }}</span>
                      </td>
                      <td class="text-center">
                        <span class="font-weight-boldest">{{ amountFormatter.formatNumber(history.sold)  + ' ' + currency.iso }}</span>
                      </td>
                    </tr>
                  </tbody>
                  <div v-else>
                    <div class="text-center">
                      <div class="pt-8">
                        <Img filename="empty.png" alt="" class="mw-100 h-100px h-sm-150px" />
                      </div>
                    </div>
                  </div>
                </table>
              </div>
            </div>
          </div>
        </template>
      </Card>
    </div>

    <AccountModal id="account_modal_create" :form="formAccount" @load="loadAccounts" :societyId="props.society" @account="setCurrentAccount"/>
    <AccountModal id="account_modal_edit" :form="formEditAccount" @load="loadAccounts" :societyId="props.society" :edit="true"/>

    <DepositModal id="deposit_modal" @load="reloadData" :form="formDeposit"/>
    <WithdrawalModal id="withdrawal_modal" @load="reloadData" :form="formWithdrawal" :officeId="props.office"/>
    <TransferModal id="transfer_modal" @load="reloadData" :form="formTransfer" :deviseExchange="currencyIso" :getAccountsToCredit="getAccountsToCredit" :getBoxes="boxes"/>
    <TransactionAccountModal id="detailsTransactionAccount" v-model="transactionAccount"/>
  </div>
</template>

<style scoped>

#kt_charts_widget_1_chart {
  width: 100%;
  height: 400px;
}

.tableFixHead {
  table-layout: fixed;
  border-collapse: collapse;
}


.tableFixHead thead tr {
  display: block;
}

.tableFixHead th,
.tableFixHead  td {
  padding: 5px 10px;
  width: 200px;
}

tbody::-webkit-scrollbar-track
{
  -webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.3);
  border-radius: 10px;
  background-color: #F5F5F5;
}

tbody::-webkit-scrollbar
{
  width: 12px;
  background-color: #F5F5F5;
}

tbody::-webkit-scrollbar-thumb
{
  border-radius: 10px;
  -webkit-box-shadow: inset 0 0 6px rgba(0,0,0,.3);
  background-color: #bcbaba;
}

tbody {
  height: calc(100vh - 350px) !important;
}

tbody tr{
  height: auto !important;
}

tbody{
  flex: 1 1 auto;
  display: block;
  overflow-y: auto;
  overflow-x: hidden;
}

</style>
